import * as React from 'react'
import {
  ComboBox as AriaComboBox,
  ComboBoxStateContext,
  Input as AriaInput,
  Button as AriaButton,
  Group,
  Popover,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Header,
  Label,
  Text,
  FieldError,
  type ComboBoxProps as AriaComboBoxProps,
  type ListBoxItemProps as AriaListBoxItemProps,
} from 'react-aria-components'
import { CaretDown, Check, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Combobox — a single-select, *searchable* type-ahead: the input filters the
 * list as you type. Built on react-aria-components. Use it for large lists
 * ("search a country/user"); for a plain dropdown use `Select`, for picking
 * several values use `MultiCombobox`.
 *
 *   <Combobox placeholder="Search fruit" aria-label="Fruit">
 *     <ComboboxInput />
 *     <ComboboxContent>
 *       <ComboboxItem id="apple">Apple</ComboboxItem>
 *     </ComboboxContent>
 *   </Combobox>
 *
 * Same input-box look + DS menu + light-basil selected row as `Select`. Boolean
 * props follow DS naming and map to react-aria's `is*`. Opens on focus/click by
 * default (`menuTrigger="focus"`, matching `MultiCombobox`); pass
 * `menuTrigger="input"` for type-to-open instead. Fills its container width
 * (`w-full`, like `Input`) — size it via the parent or a `className` width.
 */

type ComboboxSize = 'sm' | 'md' | 'lg'
type ComboboxStatus = 'default' | 'error' | 'success'

const ComboboxContext = React.createContext<{
  size: ComboboxSize
  status: ComboboxStatus
  disabled: boolean
}>({ size: 'md', status: 'default', disabled: false })
const useComboboxCtx = () => React.useContext(ComboboxContext)

const groupSize: Record<ComboboxSize, string> = {
  sm: 'h-8 pl-2.5 pr-1 text-xs',
  md: 'h-9 pl-3 pr-1 text-sm',
  lg: 'h-10 pl-3.5 pr-1 text-base',
}

const groupBase =
  'inline-flex w-full min-w-44 items-center gap-1 rounded-lg border bg-fbui-base-white text-fbui-gray-800 transition-colors focus-within:ring-4 data-[disabled]:cursor-not-allowed data-[disabled]:bg-fbui-gray-50'

const groupStatus: Record<ComboboxStatus, string> = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-600 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-600 focus-within:ring-fbui-ring-success',
}

const itemSize: Record<ComboboxSize, string> = {
  sm: 'gap-2 px-2.5 py-1.5 text-xs',
  md: 'gap-2.5 px-3 py-2 text-sm',
  lg: 'gap-2.5 px-3.5 py-2.5 text-base',
}

const glyphSize: Record<ComboboxSize, string> = {
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-4',
}

export interface ComboboxProps extends Omit<
  AriaComboBoxProps<object>,
  'isDisabled' | 'isRequired' | 'isInvalid' | 'className' | 'children'
> {
  size?: ComboboxSize
  status?: ComboboxStatus
  disabled?: boolean
  required?: boolean
  invalid?: boolean
  className?: string
  children?: React.ReactNode
}

export const Combobox = ({
  size = 'md',
  status,
  disabled,
  required,
  invalid,
  className,
  children,
  // Open the listbox on click/focus (like MultiCombobox), not only on typing —
  // react-aria's own default is `'input'`, which leaves a bare click doing
  // nothing. Overridable for the type-to-open behavior when that's wanted.
  menuTrigger = 'focus',
  ...props
}: ComboboxProps) => {
  const resolved: ComboboxStatus = invalid ? 'error' : (status ?? 'default')
  return (
    <ComboboxContext.Provider
      value={{ size, status: resolved, disabled: Boolean(disabled) }}
    >
      <AriaComboBox
        isDisabled={disabled}
        isRequired={required}
        isInvalid={invalid}
        menuTrigger={menuTrigger}
        className={cn('group flex w-full flex-col gap-1.5', className)}
        {...props}
      >
        {children}
      </AriaComboBox>
    </ComboboxContext.Provider>
  )
}
Combobox.displayName = 'Combobox'

export const ComboboxLabel = ({
  className,
  ...props
}: React.ComponentProps<typeof Label>) => {
  const { size } = useComboboxCtx()
  return (
    <Label
      className={cn(
        'font-medium text-fbui-text-title group-data-[disabled]:text-fbui-text-disabled',
        size === 'sm' ? 'text-xs' : 'text-sm',
        className
      )}
      {...props}
    />
  )
}

export const ComboboxDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof Text>) => (
  <Text
    slot="description"
    className={cn('text-xs text-fbui-text-secondary', className)}
    {...props}
  />
)

export const ComboboxErrorMessage = ({
  className,
  ...props
}: React.ComponentProps<typeof FieldError>) => (
  <FieldError
    className={cn('text-xs text-fbui-red-700', className)}
    {...props}
  />
)

export interface ComboboxInputProps {
  placeholder?: string
  className?: string
}

/** The filtering text input + clear + caret toggle, styled like our `Input` box. */
export const ComboboxInput = ({
  placeholder,
  className,
}: ComboboxInputProps) => {
  const { size, status, disabled } = useComboboxCtx()
  const state = React.useContext(ComboBoxStateContext)
  const showClear = !disabled && Boolean(state?.inputValue)
  return (
    <Group
      className={cn(groupBase, groupSize[size], groupStatus[status], className)}
    >
      <AriaInput
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-fbui-gray-800 outline-none placeholder:text-fbui-gray-400 disabled:cursor-not-allowed disabled:text-fbui-text-disabled"
      />
      {showClear && (
        <button
          type="button"
          aria-label="Clear"
          onClick={() => {
            state?.setSelectedKey(null)
            state?.setInputValue('')
          }}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded text-fbui-gray-500 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
        >
          <X weight="bold" className="size-3.5" aria-hidden="true" />
        </button>
      )}
      <AriaButton className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fbui-gray-500 outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[hovered]:text-fbui-gray-600 data-[focus-visible]:ring-2 data-[focus-visible]:ring-fbui-ring-brand">
        <CaretDown weight="bold" className="size-3.5" aria-hidden="true" />
      </AriaButton>
    </Group>
  )
}

export interface ComboboxContentProps {
  children?: React.ReactNode
  /** Message when the filter matches nothing. */
  emptyMessage?: React.ReactNode
  className?: string
}

export const ComboboxContent = ({
  children,
  emptyMessage = 'No results',
  className,
}: ComboboxContentProps) => (
  <Popover
    offset={6}
    className="z-50 overflow-hidden rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg outline-none"
  >
    <ListBox
      renderEmptyState={() => (
        <div className="px-3 py-6 text-center text-sm text-fbui-gray-500">
          {emptyMessage}
        </div>
      )}
      className={cn(
        'fbui-scroll max-h-72 w-[var(--trigger-width)] overflow-y-auto outline-none',
        className
      )}
    >
      {children}
    </ListBox>
  </Popover>
)

export interface ComboboxItemProps extends Omit<
  AriaListBoxItemProps,
  'children'
> {
  children?: React.ReactNode
  /** Leading icon/avatar. */
  icon?: React.ReactNode
}

export const ComboboxItem = ({
  className,
  children,
  icon,
  textValue,
  ...props
}: ComboboxItemProps) => {
  const { size } = useComboboxCtx()
  // The item's children are a render function, so react-aria can't derive the
  // text value — without it the input goes blank after selection. Fall back to
  // string children; rich children must pass `textValue` explicitly.
  const resolvedTextValue =
    textValue ?? (typeof children === 'string' ? children : undefined)
  return (
    <ListBoxItem
      textValue={resolvedTextValue}
      className={cn(
        'flex cursor-pointer items-center rounded-lg text-fbui-gray-700 outline-none select-none',
        'data-[focused]:bg-fbui-gray-100 data-[focused]:text-fbui-gray-900',
        'data-[selected]:bg-fbui-basil-50 data-[selected]:font-medium data-[selected]:text-fbui-basil-700',
        'data-[disabled]:pointer-events-none data-[disabled]:text-fbui-gray-300',
        itemSize[size],
        className
      )}
      {...props}
    >
      {(renderProps) => (
        <>
          {icon && (
            <span
              className={cn(
                'flex shrink-0 items-center justify-center text-fbui-gray-500',
                glyphSize[size]
              )}
            >
              {icon}
            </span>
          )}
          <span className="flex-1 truncate">{children}</span>
          {renderProps.isSelected && (
            <Check
              weight="bold"
              aria-hidden="true"
              className={cn('shrink-0 text-fbui-basil-700', glyphSize[size])}
            />
          )}
        </>
      )}
    </ListBoxItem>
  )
}

export const ComboboxSection = ({
  className,
  ...props
}: React.ComponentProps<typeof ListBoxSection>) => (
  <ListBoxSection
    className={cn(
      '[&:not(:first-child)]:mt-1.5 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-fbui-gray-200 [&:not(:first-child)]:pt-1.5',
      className
    )}
    {...props}
  />
)

export const ComboboxHeader = ({
  className,
  ...props
}: React.ComponentProps<typeof Header>) => (
  <Header
    className={cn(
      'px-3 pb-1 pt-2 text-xs font-medium text-fbui-gray-500',
      className
    )}
    {...props}
  />
)
